import { useState, useCallback, RefObject, useEffect } from "react";

interface UseIsClosingOptions {
  /**
   * Duration of the closing animation in milliseconds
   * @default 200
   */
  duration?: number;
  /**
   * Callback to run after the closing animation completes
   */
  onClose?: () => void;
  /**
   * When passed, the fade-out class will be added to @ScreenOverlay on close
   */
  overlayRef?: RefObject<HTMLElement>;
}

/**
 * Hook to manage the closing animation state of a component
 * @interface UseIsClosingOptions
 * @returns Object containing isClosing state and handleClose function
 */
export function useIsClosing({
  duration = 200,
  onClose,
  overlayRef,
}: UseIsClosingOptions) {
  const [isClosing, setIsClosing] = useState(false);
  const [timeoutId, setTimeoutId] = useState<number>();

  const handleClose = useCallback(() => {
    if (duration <= 0) {
      console.warn(
        "useIsClosing: Duration must be positive. Executing onClose immediately."
      );
      onClose?.();
      return;
    }

    setIsClosing(true);

    if (overlayRef?.current) {
      overlayRef.current.classList.add("proton-ScreenOverlay__fade-out");
    }

    const id = setTimeout(() => {
      onClose?.();
      setIsClosing(false);
    }, duration);

    setTimeoutId(id);
  }, [duration, onClose, overlayRef]);

  useEffect(() => {
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [timeoutId]);

  return {
    isClosing,
    handleClose,
  };
}
